import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { get } from 'Core/utils/platformApi';

import cacheKeys from 'Core/hooks/queries/cacheKeys';
import useAlertStore, { setErrorAlertSelector } from 'Core/stores/alert';
import useErrorStore, { setErrorActionSelector } from 'Core/stores/error';
import { errorPath } from 'Core/routes';
import { useNavigate } from 'react-location';
import { actions, features, postLogMessage } from 'Core/utils/logger';

const DATA_NOT_READY = 'Data not ready';

export default function useGetApplication(requestId, refetchInterval = false) {
  const { t } = useTranslation();
  const setError = useErrorStore(setErrorActionSelector);
  const setErrorAlert = useAlertStore(setErrorAlertSelector);

  const navigate = useNavigate();
  return useQuery(
    [cacheKeys.fortivaGetApplication, requestId],
    async () => {
      const data = await get(`/application`, { headers: { request_id: requestId } });
      if (!data?.next_action) {
        throw new Error(DATA_NOT_READY);
      }
      return data;
    },
    {
      enabled: Boolean(requestId),
      retry: (failureCount, error) => {
        if (error.message === DATA_NOT_READY) {
          return failureCount < 30;
        }
        return false;
      },
      retryDelay: 3000,
      refetchInterval,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
      onError(e) {
        if (e.message === DATA_NOT_READY) {
          setErrorAlert(t('status.error.getFinancingApplicationFailed'));
          return;
        }
        postLogMessage({
          feature: features.createAllegroApp,
          action: actions.createApplicationTimeout,
        });
        const message = e?.message ? ` - ${e?.message}` : '';
        setError(t('status.error.getFinancingApplicationFailed') + message);
        navigate({ to: errorPath, search: true });
      },
    }
  );
}
